import React, { useMemo } from "react";
import PropTypes from "prop-types";
import TimeGrid from "react-big-calendar/lib/TimeGrid";
import moment from "moment";
import { Navigate } from "react-big-calendar";

const EventCustomWeek = ({ date, localizer, ...props }) => {
    const currRange = useMemo(
        () => EventCustomWeek.range(date, props.minHour, props.eventDates),
        [date, props.minHour, props.eventDates]
    );
    return (
        <TimeGrid
            timeslots={1}
            dayLayoutAlgorithm="no-overlap"
            date={date}
            localizer={localizer}
            min={new Date(date.setHours(props.minHour.hour, props.minHour.min, 0))}
            max={new Date(date.setHours(props.maxHour.hour, props.maxHour.min, 0))}
            range={currRange}
            {...props}
        />
    );
};

EventCustomWeek.propTypes = {
    date: PropTypes.instanceOf(Date).isRequired,
    localizer: PropTypes.object.isRequired,
    max: PropTypes.instanceOf(Date),
    min: PropTypes.instanceOf(Date),
    eventDates: PropTypes.array
};

const parseEventDates = (eventDates) => {
    if (!Array.isArray(eventDates) || !eventDates.length)
        return [];

    return eventDates
        .filter(Boolean)
        .map(dateString => new Date(`${dateString}T00:00:00`))
        .sort((a, b) => a - b);
};

const toDateOnly = (date) => {
    const copy = new Date(date);
    copy.setHours(0, 0, 0, 0);
    return copy;
};

EventCustomWeek.range = (date, minHour, eventDates) => {
    const maxDays = parseInt(localStorage.getItem("MAX-DAYS-CALENDAR") || 2);
    const parsedEventDates = parseEventDates(eventDates);

    if (parsedEventDates.length) {
        const start = toDateOnly(date);
        const validDates = parsedEventDates.filter(d => toDateOnly(d) >= start);
        const range = (validDates.length ? validDates : parsedEventDates).slice(0, maxDays);
        return range.map(d => {
            const withHour = new Date(d);
            withHour.setHours(minHour.hour, minHour.min, 0);
            return withHour;
        });
    }

    const start = new Date(date);
    const end = new Date(start);
    start.setHours(minHour.hour, minHour.min, 0)
    end.setDate(start.getDate() + maxDays);
    const range = [];
    for (let current = new Date(start); current < end;  current.setDate(current.getDate() + 1) ) {
        range.push(new Date(current));
    }
    return range;
};

EventCustomWeek.navigate = (date, action, props) => {
    const newDate = new Date(date);
    const maxDays = parseInt(localStorage.getItem("MAX-DAYS-CALENDAR") || 2);
    const parsedEventDates = parseEventDates(props?.eventDates);

    if (parsedEventDates.length) {
        const current = toDateOnly(date);
        switch (action) {
            case Navigate.PREVIOUS: {
                const earlier = parsedEventDates.filter(d => toDateOnly(d) < current);
                if (earlier.length) {
                    const index = Math.max(0, earlier.length - maxDays);
                    return new Date(earlier[index]);
                }
                return new Date(parsedEventDates[0]);
            }
            case Navigate.NEXT: {
                const laterOrEqualIndex = parsedEventDates.findIndex(d => toDateOnly(d) >= current);
                const startIndex = laterOrEqualIndex === -1 ? parsedEventDates.length : laterOrEqualIndex;
                const nextIndex = startIndex + maxDays;
                if (nextIndex < parsedEventDates.length) {
                    return new Date(parsedEventDates[nextIndex]);
                }
                return new Date(parsedEventDates[parsedEventDates.length - 1]);
            }
            default:
                return date;
        }
    }

    switch (action) {
        case Navigate.PREVIOUS:
            newDate.setDate(newDate.getDate() - maxDays);
            return newDate;
        case Navigate.NEXT:
            newDate.setDate(newDate.getDate() + maxDays);
            return newDate;

        default:
            return date;
    }
};

EventCustomWeek.title = (date) => {
    return moment(date).format("MMMM");
};

export default EventCustomWeek;